import { useEffect, useState } from "react";

export const useIsContentOverflows = <T extends HTMLElement>(
  ref: React.RefObject<T>,
  options?: {
    enabled?: boolean;
  },
) => {
  const [isOverflowing, setIsOverflowing] = useState(false);
  const { enabled = true } = options ?? {};

  useEffect(() => {
    if (!enabled) {
      setIsOverflowing(false);
      return;
    }

    const updateOverflowState = () => {
      const el = ref.current;

      if (el) {
        setIsOverflowing(Boolean(el.scrollWidth > el.clientWidth));
      }
    };

    updateOverflowState();
    window.addEventListener("resize", updateOverflowState);

    // A window resize is not the only thing that changes the element's box:
    // a side pane opening, fonts finishing loading, or sibling content
    // reflowing all change available width without firing a window resize.
    // Observe the element itself so the overflow state stays correct instead
    // of being stuck on the single mount measurement.
    const observer = new ResizeObserver(updateOverflowState);
    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      window.removeEventListener("resize", updateOverflowState);
      observer.disconnect();
    };
  }, [ref, enabled]);

  return isOverflowing;
};
